import React, { useState, useEffect } from 'react';

interface Product {
  id: string;
  name: string;
  barcode: string;
  category: string;
  manufacturer: string;
}

interface BatchData {
  productId: string;
  batchCode: string;
  quantity: number;
  mfgDate: string;
  expDate: string;
  notes: string;
}

const BatchUpload: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [batchData, setBatchData] = useState<BatchData>({
    productId: '',
    batchCode: '',
    quantity: 0,
    mfgDate: '',
    expDate: '',
    notes: ''
  });
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [estimatedCost, setEstimatedCost] = useState(0);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Animation styles
  const animationStyles = `
    .fade-in-up {
      animation: fadeInUp 0.8s ease-out forwards;
    }
    
    .slide-in-left {
      animation: slideInLeft 0.8s ease-out forwards;
    }
    
    .scale-in {
      animation: scaleIn 0.6s ease-out forwards;
    }
    
    .pulse {
      animation: pulse 2s ease-in-out infinite;
    }
    
    @keyframes fadeInUp {
      from {
        opacity: 0;
        transform: translateY(30px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }
    
    @keyframes slideInLeft {
      from {
        opacity: 0;
        transform: translateX(-50px);
      }
      to {
        opacity: 1;
        transform: translateX(0);
      }
    }
    
    @keyframes scaleIn {
      from {
        opacity: 0;
        transform: scale(0.9);
      }
      to {
        opacity: 1;
        transform: scale(1);
      }
    }
    
    @keyframes pulse {
      0%, 100% {
        transform: scale(1);
      }
      50% {
        transform: scale(1.05);
      }
    }
    
    @keyframes spin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }
    
    .progress-bar {
      transition: width 0.3s ease;
    }
  `;

  useEffect(() => {
    fetchProducts();
  }, []);

  useEffect(() => {
    // Calculate estimated cost based on quantity
    const cost = batchData.quantity * 1; // KSh 1 per product
    setEstimatedCost(cost);
  }, [batchData.quantity]);

  const fetchProducts = async () => {
    try {
      // TODO: Replace with actual API call
      // Enhanced simulated data for universal platform
      setProducts([
        { id: '1', name: 'Premium Vodka 750ml', barcode: '1234567890123', category: 'Alcohol & Beverages', manufacturer: 'Kenya Spirits Ltd' },
        { id: '2', name: 'Organic Face Cream 50ml', barcode: '1234567890124', category: 'Cosmetics & Personal Care', manufacturer: 'Beauty Co Kenya' },
        { id: '3', name: 'Smartphone XR Pro', barcode: '1234567890125', category: 'Electronics', manufacturer: 'Tech Solutions Ltd' },
        { id: '4', name: 'Premium Coffee Beans 500g', barcode: '1234567890126', category: 'Food & Snacks', manufacturer: 'Kenya Coffee Co' },
        { id: '5', name: 'Pain Relief Tablets', barcode: '1234567890127', category: 'Pharmaceuticals', manufacturer: 'Pharma Kenya Ltd' }
      ]);
    } catch (error) {
      console.error('Failed to fetch products:', error);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setBatchData(prev => ({
      ...prev,
      [name]: name === 'quantity' ? parseInt(value) || 0 : value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setUploadProgress(0);

    try {
      // Simulate upload progress
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 200);

      // TODO: Replace with actual API call
      const response = await fetch('/api/batches', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(batchData)
      });

      // Simulate final progress
      setTimeout(() => {
        setUploadProgress(100);
        clearInterval(progressInterval);
        
        setTimeout(() => {
          if (response.ok || true) { // Simulate success for demo
            setUploadSuccess(true);
            // Reset form
            setBatchData({
              productId: '',
              batchCode: '',
              quantity: 0,
              mfgDate: '',
              expDate: '',
              notes: ''
            });
          } else {
            alert('Failed to upload batch. Please try again.');
          }
          setLoading(false);
          setUploadProgress(0);
        }, 500);
      }, 1000);

    } catch (error) {
      alert('Failed to upload batch. Please check your connection.');
      setLoading(false);
      setUploadProgress(0);
    }
  };

  const selectedProduct = products.find(p => p.id === batchData.productId);

  if (uploadSuccess) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)',
        padding: '40px 20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <style>{animationStyles}</style>
        
        {/* Background Pattern */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `radial-gradient(circle at 20% 80%, rgba(34, 197, 94, 0.1) 0%, transparent 50%),
                           radial-gradient(circle at 80% 20%, rgba(59, 130, 246, 0.1) 0%, transparent 50%)`,
        }}></div>

        <div className="scale-in" style={{
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          borderRadius: '24px',
          padding: '60px 40px',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          maxWidth: '700px',
          width: '100%',
          textAlign: 'center',
          position: 'relative',
          zIndex: 1
        }}>
          <div style={{
            width: '120px',
            height: '120px',
            background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
            borderRadius: '30px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 32px',
            position: 'relative'
          }}>
            <div style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '120px',
              height: '120px',
              border: '3px solid #22c55e',
              borderRadius: '30px',
              opacity: 0.3
            }} className="pulse"></div>
            
            <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
              <polyline points="20,6 9,17 4,12"/>
            </svg>
          </div>

          <h1 style={{
            color: '#22c55e',
            fontSize: '2.5rem',
            fontWeight: '800',
            marginBottom: '16px',
            letterSpacing: '-0.02em'
          }}>
            Batch Upload Complete!
          </h1>
          
          <p style={{
            color: '#64748b',
            fontSize: '20px',
            lineHeight: '1.6',
            marginBottom: '40px',
            maxWidth: '500px',
            margin: '0 auto 40px'
          }}>
            Successfully processed {batchData.quantity.toLocaleString()} product tokens. 
            Your products are now protected with enterprise-grade verification.
          </p>
          
          {/* Enhanced Batch Details */}
          <div style={{
            background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
            border: '2px solid #22c55e',
            borderRadius: '16px',
            padding: '32px',
            marginBottom: '40px',
            textAlign: 'left'
          }}>
            <h3 style={{
              color: '#166534',
              marginBottom: '20px',
              fontSize: '1.3rem',
              fontWeight: '600',
              textAlign: 'center',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"/>
              </svg>
              Batch Summary
            </h3>
            
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '20px'
            }}>
              <div style={{
                background: 'white',
                padding: '16px',
                borderRadius: '10px',
                border: '1px solid #bbf7d0'
              }}>
                <div style={{ color: '#166534', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase', marginBottom: '4px' }}>
                  Product
                </div>
                <div style={{ color: '#15803d', fontSize: '16px', fontWeight: '600' }}>
                  {selectedProduct?.name}
                </div>
                <div style={{ color: '#22c55e', fontSize: '12px', marginTop: '4px' }}>
                  {selectedProduct?.category}
                </div>
              </div>

              <div style={{
                background: 'white',
                padding: '16px',
                borderRadius: '10px',
                border: '1px solid #bbf7d0'
              }}>
                <div style={{ color: '#166534', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase', marginBottom: '4px' }}>
                  Batch Code
                </div>
                <div style={{ color: '#15803d', fontSize: '16px', fontWeight: '600', fontFamily: 'monospace' }}>
                  {batchData.batchCode}
                </div>
              </div>

              <div style={{
                background: 'white',
                padding: '16px',
                borderRadius: '10px',
                border: '1px solid #bbf7d0'
              }}>
                <div style={{ color: '#166534', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase', marginBottom: '4px' }}>
                  Quantity
                </div>
                <div style={{ color: '#15803d', fontSize: '16px', fontWeight: '600' }}>
                  {batchData.quantity.toLocaleString()} units
                </div>
              </div>

              <div style={{
                background: 'white',
                padding: '16px',
                borderRadius: '10px',
                border: '1px solid #bbf7d0'
              }}>
                <div style={{ color: '#166534', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase', marginBottom: '4px' }}>
                  Total Cost
                </div>
                <div style={{ color: '#15803d', fontSize: '16px', fontWeight: '600' }}>
                  KSh {estimatedCost.toLocaleString()}
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div style={{
            display: 'flex',
            gap: '16px',
            justifyContent: 'center',
            flexWrap: 'wrap'
          }}>
            <button
              onClick={() => setUploadSuccess(false)}
              style={{
                background: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
                color: 'white',
                padding: '16px 32px',
                border: 'none',
                borderRadius: '12px',
                fontWeight: '600',
                fontSize: '16px',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 8px 25px rgba(59, 130, 246, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 5v14M5 12l7 7 7-7"/>
              </svg>
              Upload Another Batch
            </button>
            
            <a
              href="/manufacturer/dashboard"
              style={{
                backgroundColor: 'white',
                color: '#475569',
                padding: '16px 32px',
                textDecoration: 'none',
                border: '2px solid #e2e8f0',
                borderRadius: '12px',
                fontWeight: '600',
                fontSize: '16px',
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = '#3b82f6';
                e.currentTarget.style.color = '#3b82f6';
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = '#e2e8f0';
                e.currentTarget.style.color = '#475569';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="3" width="7" height="7"/>
                <rect x="14" y="3" width="7" height="7"/>
                <rect x="14" y="14" width="7" height="7"/>
                <rect x="3" y="14" width="7" height="7"/>
              </svg>
              View Dashboard
            </a>
            
            <a
              href="/manufacturer/analytics"
              style={{
                backgroundColor: 'transparent',
                color: '#64748b',
                padding: '16px 32px',
                textDecoration: 'none',
                border: '2px solid #e2e8f0',
                borderRadius: '12px',
                fontWeight: '600',
                fontSize: '16px',
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = '#22c55e';
                e.currentTarget.style.color = '#22c55e';
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = '#e2e8f0';
                e.currentTarget.style.color = '#64748b';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 3v18h18"/>
                <path d="M18.7 8l-5.1 5.2-2.8-2.7L7 14.3"/>
              </svg>
              View Analytics
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)',
      position: 'relative',
      overflow: 'hidden'
    }}>
      <style>{animationStyles}</style>
      
      {/* Background Pattern */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundImage: `radial-gradient(circle at 20% 80%, rgba(59, 130, 246, 0.1) 0%, transparent 50%),
                         radial-gradient(circle at 80% 20%, rgba(6, 182, 212, 0.1) 0%, transparent 50%),
                         radial-gradient(circle at 40% 40%, rgba(139, 92, 246, 0.1) 0%, transparent 50%)`,
      }}></div>

      <div style={{
        position: 'relative',
        zIndex: 1,
        padding: '40px 20px'
      }}>
        <div style={{
          maxWidth: '1000px',
          margin: '0 auto'
        }}>
          {/* Header */}
          <div className="fade-in-up" style={{
            textAlign: 'center',
            marginBottom: '60px'
          }}>
            <div style={{
              display: 'inline-block',
              padding: '8px 24px',
              background: 'rgba(59, 130, 246, 0.1)',
              border: '1px solid rgba(59, 130, 246, 0.2)',
              borderRadius: '50px',
              marginBottom: '24px'
            }}>
              <span style={{
                color: '#3b82f6',
                fontSize: '14px',
                fontWeight: '600',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                Production Batch Upload
              </span>
            </div>

            <h1 style={{
              fontSize: '3.5rem',
              fontWeight: '800',
              color: 'white',
              marginBottom: '24px',
              lineHeight: '1.1',
              letterSpacing: '-0.02em'
            }}>
              Upload Production{' '}
              <span style={{
                background: 'linear-gradient(135deg, #3b82f6 0%, #06b6d4 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>
                Batch
              </span>
            </h1>

            <p style={{
              fontSize: '20px',
              color: '#94a3b8',
              maxWidth: '700px',
              margin: '0 auto',
              lineHeight: '1.6'
            }}>
              Generate unique verification tokens for your production batch. 
              Each product gets cryptographic protection at just KSh 1 per unit.
            </p>
          </div>

          {/* How It Works - Enhanced */}
          <div className="slide-in-left" style={{
            background: 'rgba(255, 255, 255, 0.05)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '24px',
            padding: '40px',
            marginBottom: '40px'
          }}>
            <h3 style={{
              color: 'white',
              fontSize: '1.5rem',
              fontWeight: '600',
              marginBottom: '32px',
              textAlign: 'center'
            }}>
              How Batch Upload Works
            </h3>
            
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '32px'
            }}>
              {[
                { icon: '📦', title: '1. Select Product', desc: 'Choose from your registered products' },
                { icon: '🏷️', title: '2. Enter Details', desc: 'Batch code, quantity, and dates' },
                { icon: '🔐', title: '3. Generate Tokens', desc: 'Cryptographic tokens created instantly' },
                { icon: '✅', title: '4. Ready to Verify', desc: 'Products protected and verifiable' }
              ].map((step, index) => (
                <div key={index} style={{
                  textAlign: 'center',
                  padding: '24px',
                  background: 'rgba(255, 255, 255, 0.05)',
                  borderRadius: '16px',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  transition: 'transform 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                }}>
                  <div style={{ fontSize: '3rem', marginBottom: '16px' }}>{step.icon}</div>
                  <h4 style={{ color: 'white', marginBottom: '12px', fontSize: '16px', fontWeight: '600' }}>
                    {step.title}
                  </h4>
                  <p style={{ color: '#94a3b8', fontSize: '14px', margin: 0, lineHeight: '1.4' }}>
                    {step.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Upload Form - Enhanced */}
          <div className="fade-in-up" style={{
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            borderRadius: '24px',
            padding: '48px',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
          }}>
            {loading && (
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(255, 255, 255, 0.9)',
                borderRadius: '24px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10
              }}>
                <div style={{
                  width: '80px',
                  height: '80px',
                  border: '4px solid #f3f4f6',
                  borderTop: '4px solid #3b82f6',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                  marginBottom: '24px'
                }}></div>
                <h3 style={{ color: '#1e293b', marginBottom: '16px', fontSize: '20px', fontWeight: '600' }}>
                  Processing Batch Upload
                </h3>
                <div style={{
                  width: '300px',
                  height: '8px',
                  backgroundColor: '#e2e8f0',
                  borderRadius: '4px',
                  overflow: 'hidden',
                  marginBottom: '12px'
                }}>
                  <div 
                    className="progress-bar"
                    style={{
                      width: `${uploadProgress}%`,
                      height: '100%',
                      background: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
                      borderRadius: '4px'
                    }}
                  ></div>
                </div>
                <p style={{ color: '#64748b', fontSize: '14px' }}>
                  {uploadProgress}% complete - Generating {batchData.quantity.toLocaleString()} tokens...
                </p>
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                gap: '24px',
                marginBottom: '24px'
              }}>
                {/* Product Selection - Enhanced */}
                <div>
                  <label style={{
                    display: 'block',
                    color: '#374151',
                    fontWeight: '600',
                    marginBottom: '8px',
                    fontSize: '16px'
                  }}>
                    Select Product *
                  </label>
                  <select
                    name="productId"
                    value={batchData.productId}
                    onChange={handleInputChange}
                    required
                    style={{
                      width: '100%',
                      padding: '16px 20px',
                      border: '2px solid #e2e8f0',
                      borderRadius: '12px',
                      fontSize: '16px',
                      outline: 'none',
                      backgroundColor: 'white',
                      transition: 'all 0.3s ease'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#3b82f6';
                      e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#e2e8f0';
                      e.target.style.boxShadow = 'none';
                    }}
                  >
                    <option value="">Choose a product from your catalog</option>
                    {products.map((product) => (
                      <option key={product.id} value={product.id}>
                        {product.name} - {product.category} ({product.barcode})
                      </option>
                    ))}
                  </select>
                  {products.length === 0 && (
                    <p style={{
                      color: '#ef4444',
                      fontSize: '14px',
                      margin: '8px 0 0 0',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10"/>
                        <line x1="15" y1="9" x2="9" y2="15"/>
                        <line x1="9" y1="9" x2="15" y2="15"/>
                      </svg>
                      No products found. <a href="/manufacturer/products" style={{ color: '#3b82f6', fontWeight: '600' }}>Add a product first</a>
                    </p>
                  )}
                </div>

                {/* Batch Code - Enhanced */}
                <div>
                  <label style={{
                    display: 'block',
                    color: '#374151',
                    fontWeight: '600',
                    marginBottom: '8px',
                    fontSize: '16px'
                  }}>
                    Batch Code *
                  </label>
                  <input
                    type="text"
                    name="batchCode"
                    value={batchData.batchCode}
                    onChange={handleInputChange}
                    required
                    style={{
                      width: '100%',
                      padding: '16px 20px',
                      border: '2px solid #e2e8f0',
                      borderRadius: '12px',
                      fontSize: '16px',
                      outline: 'none',
                      fontFamily: 'monospace',
                      letterSpacing: '1px',
                      transition: 'all 0.3s ease'
                    }}
                    placeholder="e.g., BATCH240315, LOT123456"
                    onFocus={(e) => {
                      e.target.style.borderColor = '#3b82f6';
                      e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#e2e8f0';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                  <p style={{
                    color: '#64748b',
                    fontSize: '14px',
                    margin: '8px 0 0 0',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <circle cx="12" cy="12" r="10"/>
                      <path d="M9,9h6v6H9z"/>
                    </svg>
                    Enter the batch code printed on your product packaging
                  </p>
                </div>

                {/* Quantity - Enhanced */}
                <div>
                  <label style={{
                    display: 'block',
                    color: '#374151',
                    fontWeight: '600',
                    marginBottom: '8px',
                    fontSize: '16px'
                  }}>
                    Quantity *
                  </label>
                  <input
                    type="number"
                    name="quantity"
                    value={batchData.quantity || ''}
                    onChange={handleInputChange}
                    required
                    min="1"
                    max="1000000"
                    style={{
                      width: '100%',
                      padding: '16px 20px',
                      border: '2px solid #e2e8f0',
                      borderRadius: '12px',
                      fontSize: '16px',
                      outline: 'none',
                      transition: 'all 0.3s ease'
                    }}
                    placeholder="Number of units in this batch"
                    onFocus={(e) => {
                      e.target.style.borderColor = '#3b82f6';
                      e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#e2e8f0';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                  {batchData.quantity > 0 && (
                    <div style={{
                      marginTop: '8px',
                      padding: '8px 12px',
                      background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
                      border: '1px solid #bbf7d0',
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2">
                        <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
                      </svg>
                      <span style={{
                        color: '#166534',
                        fontSize: '14px',
                        fontWeight: '600'
                      }}>
                        Cost: KSh {estimatedCost.toLocaleString()} @ KSh 1 per unit
                      </span>
                    </div>
                  )}
                </div>

                {/* Manufacturing Date */}
                <div>
                  <label style={{
                    display: 'block',
                    color: '#374151',
                    fontWeight: '600',
                    marginBottom: '8px',
                    fontSize: '16px'
                  }}>
                    Manufacturing Date *
                  </label>
                  <input
                    type="date"
                    name="mfgDate"
                    value={batchData.mfgDate}
                    onChange={handleInputChange}
                    required
                    style={{
                      width: '100%',
                      padding: '16px 20px',
                      border: '2px solid #e2e8f0',
                      borderRadius: '12px',
                      fontSize: '16px',
                      outline: 'none',
                      transition: 'all 0.3s ease'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#3b82f6';
                      e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#e2e8f0';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                </div>

                {/* Expiry Date */}
                <div>
                  <label style={{
                    display: 'block',
                    color: '#374151',
                    fontWeight: '600',
                    marginBottom: '8px',
                    fontSize: '16px'
                  }}>
                    Expiry Date *
                  </label>
                  <input
                    type="date"
                    name="expDate"
                    value={batchData.expDate}
                    onChange={handleInputChange}
                    required
                    style={{
                      width: '100%',
                      padding: '16px 20px',
                      border: '2px solid #e2e8f0',
                      borderRadius: '12px',
                      fontSize: '16px',
                      outline: 'none',
                      transition: 'all 0.3s ease'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#3b82f6';
                      e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#e2e8f0';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                </div>
              </div>

              {/* Notes - Enhanced */}
              <div style={{ marginBottom: '32px' }}>
                <label style={{
                  display: 'block',
                  color: '#374151',
                  fontWeight: '600',
                  marginBottom: '8px',
                  fontSize: '16px'
                }}>
                  Additional Notes (Optional)
                </label>
                <textarea
                  name="notes"
                  value={batchData.notes}
                  onChange={handleInputChange}
                  rows={4}
                  style={{
                    width: '100%',
                    padding: '16px 20px',
                    border: '2px solid #e2e8f0',
                    borderRadius: '12px',
                    fontSize: '16px',
                    outline: 'none',
                    resize: 'vertical',
                    transition: 'all 0.3s ease'
                  }}
                  placeholder="Any additional information about this batch (quality notes, special handling, etc.)"
                  onFocus={(e) => {
                    e.target.style.borderColor = '#3b82f6';
                    e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e2e8f0';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>

              {/* Enhanced Cost Summary */}
              {batchData.quantity > 0 && (
                <div style={{
                  background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
                  border: '2px solid #3b82f6',
                  borderRadius: '16px',
                  padding: '32px',
                  marginBottom: '32px'
                }}>
                  <h4 style={{
                    color: '#1e293b',
                    fontSize: '1.3rem',
                    fontWeight: '600',
                    marginBottom: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/>
                      <line x1="1" y1="10" x2="23" y2="10"/>
                    </svg>
                    Cost Summary
                  </h4>
                  
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                    gap: '20px',
                    marginBottom: '20px'
                  }}>
                    <div style={{
                      background: 'white',
                      padding: '20px',
                      borderRadius: '12px',
                      border: '1px solid #e2e8f0',
                      textAlign: 'center'
                    }}>
                      <div style={{ color: '#64748b', fontSize: '14px', marginBottom: '8px' }}>
                        Units to Protect
                      </div>
                      <div style={{ color: '#1e293b', fontSize: '2rem', fontWeight: '700' }}>
                        {batchData.quantity.toLocaleString()}
                      </div>
                    </div>

                    <div style={{
                      background: 'white',
                      padding: '20px',
                      borderRadius: '12px',
                      border: '1px solid #e2e8f0',
                      textAlign: 'center'
                    }}>
                      <div style={{ color: '#64748b', fontSize: '14px', marginBottom: '8px' }}>
                        Price per Unit
                      </div>
                      <div style={{ color: '#1e293b', fontSize: '2rem', fontWeight: '700' }}>
                        KSh 1
                      </div>
                    </div>

                    <div style={{
                      background: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
                      padding: '20px',
                      borderRadius: '12px',
                      textAlign: 'center',
                      color: 'white'
                    }}>
                      <div style={{ fontSize: '14px', marginBottom: '8px', opacity: 0.9 }}>
                        Total Cost
                      </div>
                      <div style={{ fontSize: '2rem', fontWeight: '700' }}>
                        KSh {estimatedCost.toLocaleString()}
                      </div>
                    </div>
                  </div>

                  <div style={{
                    background: 'white',
                    padding: '16px',
                    borderRadius: '10px',
                    border: '1px solid #e2e8f0'
                  }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      fontSize: '16px'
                    }}>
                      <span style={{ color: '#64748b' }}>
                        {batchData.quantity.toLocaleString()} units × KSh 1
                      </span>
                      <span style={{ color: '#1e293b', fontWeight: '700', fontSize: '18px' }}>
                        KSh {estimatedCost.toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Submit Button - Enhanced */}
              <div style={{ textAlign: 'center' }}>
                <button
                  type="submit"
                  disabled={loading || products.length === 0 || batchData.quantity === 0}
                  style={{
                    background: loading || products.length === 0 || batchData.quantity === 0
                      ? '#9ca3af'
                      : 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
                    color: 'white',
                    padding: '20px 48px',
                    border: 'none',
                    borderRadius: '16px',
                    fontSize: '18px',
                    fontWeight: '600',
                    cursor: loading || products.length === 0 || batchData.quantity === 0 ? 'not-allowed' : 'pointer',
                    transition: 'all 0.3s ease',
                    opacity: loading || products.length === 0 || batchData.quantity === 0 ? 0.7 : 1,
                    boxShadow: loading || products.length === 0 || batchData.quantity === 0
                      ? 'none'
                      : '0 4px 15px rgba(59, 130, 246, 0.3)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    margin: '0 auto'
                  }}
                  onMouseEnter={(e) => {
                    if (!loading && products.length > 0 && batchData.quantity > 0) {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 8px 25px rgba(59, 130, 246, 0.4)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!loading && products.length > 0 && batchData.quantity > 0) {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 4px 15px rgba(59, 130, 246, 0.3)';
                    }
                  }}
                >
                  {loading ? (
                    <>
                      <div style={{
                        width: '20px',
                        height: '20px',
                        border: '2px solid rgba(255, 255, 255, 0.3)',
                        borderTop: '2px solid white',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                      }}></div>
                      Processing Batch...
                    </>
                  ) : (
                    <>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                        <polyline points="7,10 12,15 17,10"/>
                        <line x1="12" y1="15" x2="12" y2="3"/>
                      </svg>
                      {batchData.quantity > 0 
                        ? `Upload Batch (KSh ${estimatedCost.toLocaleString()})`
                        : 'Upload Production Batch'
                      }
                    </>
                  )}
                </button>

                {(products.length === 0 || batchData.quantity === 0) && (
                  <p style={{
                    color: '#64748b',
                    fontSize: '14px',
                    marginTop: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px'
                  }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10"/>
                      <line x1="12" y1="8" x2="12" y2="12"/>
                      <line x1="12" y1="16" x2="12.01" y2="16"/>
                    </svg>
                    {products.length === 0 
                      ? 'Please add products before uploading batches'
                      : 'Enter quantity to see cost estimate'
                    }
                  </p>
                )}
              </div>
            </form>
          </div>

          {/* Enhanced Help Section */}
          <div className="fade-in-up" style={{
            background: 'rgba(255, 255, 255, 0.05)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '24px',
            padding: '40px',
            marginTop: '40px'
          }}>
            <h3 style={{
              color: 'white',
              fontSize: '1.3rem',
              fontWeight: '600',
              marginBottom: '24px',
              textAlign: 'center'
            }}>
              Need Assistance?
            </h3>
            
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: '24px'
            }}>
              <div style={{
                textAlign: 'center',
                padding: '24px',
                background: 'rgba(255, 255, 255, 0.05)',
                borderRadius: '16px',
                border: '1px solid rgba(255, 255, 255, 0.1)'
              }}>
                <div style={{
                  width: '60px',
                  height: '60px',
                  background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                  borderRadius: '15px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px'
                }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                  </svg>
                </div>
                <h4 style={{ color: 'white', marginBottom: '8px', fontSize: '16px', fontWeight: '600' }}>
                  Phone Support
                </h4>
                <a
                  href="tel:+254114445264"
                  style={{
                    color: '#94a3b8',
                    textDecoration: 'none',
                    fontWeight: '500',
                    fontSize: '14px',
                    transition: 'color 0.3s ease'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.color = '#22c55e'}
                  onMouseLeave={(e) => e.currentTarget.style.color = '#94a3b8'}
                >
                  +254 114 445 264
                </a>
              </div>
              
              <div style={{
                textAlign: 'center',
                padding: '24px',
                background: 'rgba(255, 255, 255, 0.05)',
                borderRadius: '16px',
                border: '1px solid rgba(255, 255, 255, 0.1)'
              }}>
                <div style={{
                  width: '60px',
                  height: '60px',
                  background: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
                  borderRadius: '15px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px'
                }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                    <polyline points="22,6 12,13 2,6"/>
                  </svg>
                </div>
                <h4 style={{ color: 'white', marginBottom: '8px', fontSize: '16px', fontWeight: '600' }}>
                  Email Support
                </h4>
                <a
                  href="mailto:karuoyamercy@gmail.com"
                  style={{
                    color: '#94a3b8',
                    textDecoration: 'none',
                    fontWeight: '500',
                    fontSize: '14px',
                    transition: 'color 0.3s ease'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.color = '#3b82f6'}
                  onMouseLeave={(e) => e.currentTarget.style.color = '#94a3b8'}
                >
                  karuoyamercy@gmail.com
                </a>
              </div>

              <div style={{
                textAlign: 'center',
                padding: '24px',
                background: 'rgba(255, 255, 255, 0.05)',
                borderRadius: '16px',
                border: '1px solid rgba(255, 255, 255, 0.1)'
              }}>
                <div style={{
                  width: '60px',
                  height: '60px',
                  background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                  borderRadius: '15px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px'
                }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/>
                    <path d="M9,9h6v6H9z"/>
                  </svg>
                </div>
                <h4 style={{ color: 'white', marginBottom: '8px', fontSize: '16px', fontWeight: '600' }}>
                  Documentation
                </h4>
                <a
                  href="/help"
                  style={{
                    color: '#94a3b8',
                    textDecoration: 'none',
                    fontWeight: '500',
                    fontSize: '14px',
                    transition: 'color 0.3s ease'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.color = '#8b5cf6'}
                  onMouseLeave={(e) => e.currentTarget.style.color = '#94a3b8'}
                >
                  View Help Center
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BatchUpload;



